import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { listSettlementInvoices } from '../../../../services/ReconciliationActionService';
import { getStatusLabel } from '../../../../config/invoiceStatus';
import { formatCurrency, formatDate } from '../bankFormat';

const lineOre = line => Math.round(Math.abs(Number(line.amount))*100);
const PAYABLE_STATUSES = ['Sent','Partially_Paid','Approved'];
const SUPPLIER_STATUS_LABELS = { Scanned:'Inläst', Approved:'Godkänd', Paid:'Betald' };
const statusLabel = invoice => (invoice.side === 'supplier' ? SUPPLIER_STATUS_LABELS[invoice.status] : null) || getStatusLabel(invoice.status);

// Why a payment cannot be registered on this invoice from this bank line; null when it can.
const registerBlocker = (invoice,line) => {
    if(invoice.currency && invoice.currency !== 'SEK') return `Fakturan är i ${invoice.currency}. Bara fakturor i SEK kan betalas härifrån.`;
    if(!PAYABLE_STATUSES.includes(invoice.status)) return `Fakturan är ${String(statusLabel(invoice)).toLowerCase()} och kan inte ta emot en betalning.`;
    if(invoice.open_ore < lineOre(line)) return `Bankhändelsen (${formatCurrency(lineOre(line)/100)}) är större än det som återstår.`;
    return null;
};
// Why a registered payment cannot be linked to this bank line; null when it can.
const linkBlocker = (invoice,payment,line) => {
    if(invoice.currency && invoice.currency !== 'SEK') return 'Fakturan är inte i SEK.';
    if(!payment.verification_id) return 'Betalningen saknar verifikat.';
    if(payment.amount_ore !== lineOre(line)) return 'Beloppet skiljer sig från bankhändelsen.';
    return null;
};

const helpStyle = { fontSize:'0.75rem', color:'#6b7280' };
const textAction = { fontSize:'0.8125rem', textDecoration:'none' };

/**
 * The manual way to an invoice: search, then choose. Choosing is all it
 * does; `onPick(invoice, payment?)` hands the choice to the workspace, which
 * carries it out exactly as it does for an invoice the engine suggested.
 * Rows read like the invoice cards in the sheet: who and how much, which
 * invoice, and why it cannot be chosen when it cannot.
 */
export default function InvoicePickerModal({ companyId, line, busy, onPick }) {
    const [search,setSearch] = useState(''), [state,setState] = useState({ invoices:[],loading:true });
    useEffect(() => {
        let active=true;
        const timer=setTimeout(() => { setState(old => ({...old,loading:true,error:null})); listSettlementInvoices(companyId,line.id,search).then(data => {if(active)setState({...data,loading:false});}).catch(e => {if(active)setState({invoices:[],loading:false,error:e.response?.data?.message || 'Kunde inte hämta fakturor.'});}); },200);
        return () => { active=false;clearTimeout(timer); };
    },[companyId,line.id,search]);
    return <section aria-label="Välj faktura eller betalning" style={{ fontSize:'0.875rem' }}>
        <Form.Control size="sm" type="search" aria-label="Sök faktura" placeholder="Sök fakturanummer, namn eller referens" value={search} onChange={e => setSearch(e.target.value)} />
        {state.error && <Alert variant="warning" className="py-2 mt-2 mb-0" style={{ fontSize:'0.8125rem' }}>{state.error}</Alert>}
        {state.loading && <div style={{ ...helpStyle, marginTop:10 }}>Hämtar fakturor…</div>}
        {!state.loading && !state.error && state.invoices.length === 0 && <div style={{ ...helpStyle, marginTop:10 }}>Inga fakturor hittades.</div>}
        {state.invoices.map(invoice => {
            const blocker = registerBlocker(invoice,line);
            return <div key={invoice.id} className="border-bottom" style={{ padding:'10px 0' }}>
                <div className="d-flex align-items-center gap-3">
                    <div style={{ flex:1, minWidth:0 }}>
                        <div className="d-flex align-items-baseline justify-content-between gap-3">
                            <span style={{ fontWeight:600, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{invoice.counterparty_name}</span>
                            <span style={{ fontWeight:600, color:'#111827', fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>{formatCurrency(invoice.open_ore / 100)}</span>
                        </div>
                        <div style={{ ...helpStyle, marginTop:2 }}>Faktura {invoice.number} · {statusLabel(invoice)}</div>
                        {blocker && <div style={{ ...helpStyle, color:'#b45309', marginTop:2 }}>{blocker}</div>}
                    </div>
                    <Button size="sm" variant="outline-primary" style={{ flexShrink:0 }} disabled={busy || state.loading || !!blocker} onClick={() => onPick(invoice)}>Välj</Button>
                </div>
                {invoice.payments.map(payment => {
                    const linkBlocked = linkBlocker(invoice,payment,line);
                    return <div className="d-flex align-items-baseline flex-wrap" style={{ fontSize:'0.8125rem', color:'#374151', columnGap:10, marginTop:4 }} key={payment.payment_id}>
                        <span>Registrerad betalning {formatDate(payment.payment_date)} · {formatCurrency(payment.amount_ore/100)}</span>
                        {linkBlocked
                            ? <span style={helpStyle}>{linkBlocked}</span>
                            : <Button variant="link" size="sm" className="p-0" style={textAction} disabled={busy || state.loading} onClick={() => onPick(invoice,payment)}>Koppla till bankhändelsen</Button>}
                    </div>;
                })}
            </div>;
        })}
        {state.complete === false && <div style={{ ...helpStyle, marginTop:10 }}>Fler fakturor finns. Begränsa sökningen.</div>}
    </section>;
}
